import { postApiWebSession } from '/api/sdk.gen';

import type { AppDispatch, RootState } from '/business/shared/state/store';
import { sessionTTL } from '/shared/util/Statics';
import getSessionTokenSelector from '/shared/state/selectors/getSessionTokenSelector';
import normaliseError, { toNormaliseErrorInput } from '/shared/functions/normaliseError';

import getSession from './getSession';

const requestData = { ttl: sessionTTL, context: 'business' };

const COOLDOWN_MS = 10_000;

let debounce: Promise<void> | null = null;
let lastRefreshTime = 0;

const refreshSession =
  ({ force = false } = {}) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const existingToken = getSessionTokenSelector(getState());
    if (!existingToken) {
      return;
    }

    if (!force && Date.now() - lastRefreshTime < COOLDOWN_MS) {
      return;
    }

    if (debounce) {
      return debounce;
    }

    debounce = (async () => {
      try {
        const { auth: authState } = getState();

        if (authState.fetching) {
          return;
        }

        await dispatch({ type: 'GET_SESSION_STARTED', timestamp: Date.now() });

        const { data } = await postApiWebSession({
          body: { ...requestData, refresh: existingToken },
        });

        if (!data) {
          throw new Error('No data from web_session response');
        }

        const { token, expires } = data;

        if (!token) {
          throw new Error('No token from web_session response');
        }
        if (!expires) {
          throw new Error('No expires from web_session response');
        }
        if (new Date(expires).getTime() - Date.now() <= 0) {
          throw new Error('Expiry is in the past');
        }

        lastRefreshTime = Date.now();
        await dispatch({ type: 'GET_SESSION_FULFILLED', token, expires });
      } catch (error) {
        await dispatch({
          type: 'GET_SESSION_REJECTED',
          error: normaliseError(toNormaliseErrorInput(error), 'SESSION'),
        });

        try {
          await dispatch(getSession());
          return;
        } catch (_fallbackError) {
          // Fallback also failed
        }
        throw error;
      } finally {
        debounce = null;
      }
    })();

    return debounce;
  };

export default refreshSession;
